import { useRouteQuery } from "@vueuse/router";
import { find } from "lodash-es";
import {
  computed,
  ComputedRef,
  MaybeRefOrGetter,
  Ref,
  toValue,
  unref,
  watch,
} from "vue";

export type Tab = {
  // The id is used to identify the tab in the route.
  id: string;
  // The name is used to display the tab in the UI.
  name: string;
  // The disabled flag is used to disable the tab (e.g. Forward Sales for users without permission).
  disabled?: boolean;
  // The hidden flag is used to hide the tab from the UI (e.g. Forward Sales for users without permission).
  hidden?: boolean;
  // The icon is used to display a FontAwesome icon before the tab name.
  icon?: string | [string, string];
};

export const useTabs = <
  TScopeId extends string,
  TTabsInput extends Tab[] | Ref<Tab[]> | ComputedRef<Tab[]>,
  TDefaultTabIdInput extends string | null | (() => string | null),
>(
  // The scopeId is used to identify the tabs in the route with a common prefix.
  scopeId: TScopeId,
  config: {
    tabs: TTabsInput;
    defaultTabId: TDefaultTabIdInput;
    allowNoActiveTab?: boolean;
    // NOTE: while false the route's active tab is left unvalidated, so a deep link to a tab the list
    // only gains once the account loads (an admin-only tab) is not reset to the default meanwhile.
    ready?: MaybeRefOrGetter<boolean>;
  }
) => {
  const { allowNoActiveTab = false } = config;

  // Ensure tabs is a reactive ref for internal use, handling computed refs correctly
  const tabsRef = computed(() => unref(config.tabs));

  // Helper function to get the current default tab ID
  const getDefaultTabId = (): string | null => {
    const dfId = config.defaultTabId;
    if (typeof dfId === "function") {
      return dfId();
    } else {
      return dfId;
    }
  };

  const activeTab = useRouteQuery<string | null, string | null>(
    `${scopeId}-active-tab`,
    getDefaultTabId() // Use the initially resolved default
  );

  function setTab(tabId: string | null) {
    // Access current value of tabsRef
    const currentTabs = tabsRef.value;

    if (tabId === null) {
      if (allowNoActiveTab) {
        activeTab.value = null;
      }
      return;
    }

    // If the tab is already active and allowNoActiveTab is true, close it
    if (activeTab.value === tabId && allowNoActiveTab) {
      activeTab.value = null;
      return;
    }

    // Find tab in the *current* list
    const newTab = find(currentTabs, { id: tabId });
    if (!newTab) {
      console.warn(
        `Tab with id ${tabId} not found in current tabs. (Tab Scope: ${scopeId})`
      );
      return;
    }

    // Check if the found tab is disabled (using currentTabs)
    if (newTab.disabled) {
      console.warn(
        `Attempted to set disabled tab: ${tabId}. (Tab Scope: ${scopeId})`
      );
      return;
    }

    activeTab.value = newTab.id;
  }

  // Watch for changes in the tabs array
  watch(
    [tabsRef, () => toValue(config.ready ?? true)] as const,
    ([newTabs, ready]) => {
      if (!ready) return;
      const currentActiveTabId = activeTab.value;
      if (currentActiveTabId) {
        // Check if the currently active tab still exists and is not disabled in the new list
        const activeTabInfo = find(newTabs, { id: currentActiveTabId });
        if (!activeTabInfo || activeTabInfo.disabled) {
          console.warn(
            `Active tab ${currentActiveTabId} no longer valid or became disabled. Resetting. (Tab Scope: ${scopeId})`
          );
          // Reset to the potentially updated default tab ID
          activeTab.value = getDefaultTabId();
        }
      } else if (!allowNoActiveTab) {
        // If no tab is active, but one should be, ensure the default is set (if valid)
        const currentDefaultTabId = getDefaultTabId();
        if (currentDefaultTabId) {
          const defaultTabInfo = find(newTabs, { id: currentDefaultTabId });
          if (defaultTabInfo && !defaultTabInfo.disabled) {
            activeTab.value = currentDefaultTabId;
          }
        }
      }
    },
    { deep: true, immediate: true } // Use deep watch and run immediately to validate initial state
  );

  return {
    activeTab,
    setTab,
  };
};
